import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'

import { useErrorHandler } from '@/features/bidding/hooks/useErrorHandler'
import { pqClient } from '@/features/prequalification/localControl/endpoints/pqClient'
import type { ResourceSetMember } from '@/features/prequalification/localControl/types'
import type { UseQueryOptions } from '@/types/queries'

export const SEARCH_RESOURCES_API_ID = 'SEARCH_LOCAL_PQ_RESOURCES'

export type UseSearchResourcesQueryResult = Omit<UseQueryResult, 'data'> & {
  resources: ResourceSetMember[] | null
}

/**
 * Resource search for the "Search" tab of the add-resources panel. The caller
 * is expected to debounce `query`; the query stays disabled until it is
 * non-empty so an empty field never fires a request.
 */
export function useSearchResourcesQuery(
  query: string,
  options?: UseQueryOptions<ResourceSetMember[]>,
): UseSearchResourcesQueryResult {
  const trimmed = query.trim()

  const { data, ...queryResult } = useQuery({
    ...options,
    // Keep the empty-query guard authoritative: a caller's `enabled` can only
    // further restrict the search, never fire it on an empty field.
    enabled: trimmed.length > 0 && (options?.enabled ?? true),
    queryFn: () => pqClient.searchResources(trimmed),
    queryKey: [SEARCH_RESOURCES_API_ID, trimmed],
  })

  useErrorHandler(queryResult.isError)

  return {
    ...queryResult,
    resources: data ?? null,
  }
}
